import React, { useEffect, useRef, useState } from 'react';
import { FaXmark } from 'react-icons/fa6';
import aiService, { ChatMessage } from '../../services/aiService';
import ChatMessageBubble from './ChatMessage';

const INITIAL_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: 'Hi! I\'m your VeraConnect assistant. How can I help you with the dashboard today?',
};

export default function ChatAssistant(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestsLeft, setRequestsLeft] = useState<number | null>(null);
  const [rateLimitError, setRateLimitError] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const animationId = window.requestAnimationFrame(() => {
      messagesContainerRef.current?.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });

    return () => window.cancelAnimationFrame(animationId);
  }, [open, messages, loading, rateLimitError]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setRateLimitError(false);

    try {
      const history = updatedMessages.slice(1); // exclude the initial assistant greeting
      const response = await aiService.sendMessage(trimmed, history);
      setMessages((prev) => [...prev, { role: 'assistant', content: response.reply }]);
      setRequestsLeft(response.requestsLeft);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setRateLimitError(true);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'Something went wrong. Please try again.' },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send().catch(() => {
        // send handles errors internally
      });
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary-900 text-white shadow-lg hover:bg-opacity-90 transition-all"
        title="VeraConnect Assistant">
        {open ? (
          <FaXmark className="h-6 w-6" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 left-4 right-4 z-50 flex h-[min(72vh,44rem)] max-h-[44rem] w-auto flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-2xl sm:left-auto sm:right-6 sm:w-[24rem] md:w-[26rem]">
          {/* Header */}
          <div className="flex items-center justify-between bg-primary-900 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white bg-opacity-20">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-white">VeraConnect Assistant</p>
                <p className="text-xs text-white text-opacity-70">Dashboard help</p>
              </div>
            </div>
            {requestsLeft !== null && (
              <span className="rounded-full bg-white bg-opacity-20 px-2 py-0.5 text-xs text-white">
                {requestsLeft} left
              </span>
            )}
          </div>

          {/* Messages */}
          <div
            ref={messagesContainerRef}
            className="no-scrollbar flex flex-1 flex-col gap-3 overflow-y-auto p-4">
            {messages.map((msg, i) => (
              <ChatMessageBubble key={i} message={msg} />
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-tl-sm bg-secondary-200 px-4 py-2 text-sm text-primary-900">
                  <span className="inline-flex gap-1">
                    <span className="animate-bounce delay-0">·</span>
                    <span className="animate-bounce delay-150">·</span>
                    <span className="animate-bounce delay-300">·</span>
                  </span>
                </div>
              </div>
            )}
            {rateLimitError && (
              <p className="text-center text-xs text-red-500">
                You&apos;ve reached your hourly limit. Please try again later.
              </p>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 border-t border-secondary-200 px-3 py-3">
            <input
              type="text"
              className="flex-1 rounded-full border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm outline-none focus:border-primary-900"
              placeholder="Ask about the dashboard..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || rateLimitError}
            />
            <button
              type="button"
              onClick={() => {
                send().catch(() => {
                  // send handles errors internally
                });
              }}
              disabled={loading || !input.trim() || rateLimitError}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-900 text-white disabled:opacity-40 hover:bg-opacity-90 transition-opacity">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
